import React, { forwardRef } from "react";
import { Tooltip } from "antd";
import {
  downloadNodeAsPng,
  DownloadPngButton,
  PngExportFrame,
} from "../common/pngExport";

// The PNG export machinery moved to components/common/pngExport so shared UI
// and utils never import from a domain folder; re-exported here for compat.
export { downloadNodeAsPng, DownloadPngButton, PngExportFrame };

export const numberFormatter = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

export const formatINR = (n) =>
  n === null || n === undefined || isNaN(n) ? "—" : `₹${numberFormatter.format(n)}`;

/** White section card — the standard chrome every OPD section sits in. */
export const SectionCard = forwardRef(({ children, className = "" }, ref) => (
  <div ref={ref} className={`rounded-[14px] border border-gray-300 bg-white p-6 ${className}`}>
    {children}
  </div>
));

/**
 * Chapter header row: numbered mono index + uppercase eyebrow on the left,
 * controls (segmented toggles / selects) on the right.
 */
export const ChapterHeader = ({ number, title, subtitle, controls, downloadRef, downloadName }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        {number && (
          <span className="font-jetbrains-mono text-[11px] font-semibold tabular-nums text-blue-700">
            {number}
          </span>
        )}
        <span className="text-[11px] font-fig-semibold uppercase tracking-[0.1em] text-gray-600">
          {title}
        </span>
        {downloadRef && <DownloadPngButton nodeRef={downloadRef} fileName={downloadName || title} />}
      </div>
      {subtitle && <p className="mb-0 mt-1 text-[13px] font-fig-normal text-gray-600">{subtitle}</p>}
    </div>
    {controls && <div className="flex flex-wrap items-center gap-2">{controls}</div>}
  </div>
);

/** Segmented pill control — active option filled with brand blue. */
export const SegmentedControl = ({ options = [], value, onChange = () => {} }) => (
  <div className="inline-flex items-center gap-0.5 rounded-[10px] border border-gray-300 bg-gray-100 p-[3px]">
    {options.map((opt) => {
      const isActive = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded-lg px-3.5 py-1.5 text-xs transition-colors duration-150 focus:outline-none ${
            isActive
              ? "bg-blue-700 font-fig-semibold text-white"
              : "bg-transparent font-fig-normal text-gray-700 hover:text-gray-900"
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

/**
 * Horizontal 100%-stacked bar. segments: [{ value, color, label, tooltip }].
 * Labels render inside a segment only when it is wide enough to fit them.
 */
export const StackedBar = ({ segments = [], height = 12, className = "" }) => {
  const total = segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  return (
    <div
      className={`flex w-full overflow-hidden rounded-full bg-gray-200 ${className}`}
      style={{ height }}
    >
      {total > 0 &&
        segments.map((seg, idx) => {
          const pct = ((Number(seg.value) || 0) / total) * 100;
          if (pct <= 0) return null;
          const bar = (
            <div
              className="flex h-full items-center justify-end"
              style={{ width: `${pct}%`, backgroundColor: seg.color, minWidth: pct < 2 ? 4 : undefined }}
            >
              {seg.label && pct >= 14 && (
                <span className="truncate px-2 font-jetbrains-mono text-[11px] font-semibold tabular-nums text-white">
                  {seg.label}
                </span>
              )}
            </div>
          );
          return (
            <Tooltip key={idx} title={seg.tooltip}>
              {bar}
            </Tooltip>
          );
        })}
    </div>
  );
};

/** 9×9 legend swatch. */
export const LegendSwatch = ({ color, className = "" }) => (
  <span
    className={`inline-block h-[9px] w-[9px] shrink-0 rounded-sm ${className}`}
    style={{ backgroundColor: color }}
  />
);

/** Uppercase table header cell text style. */
export const thClass =
  "text-[11px] font-fig-semibold uppercase tracking-[0.06em] text-gray-500";

/** Pulsing skeleton line for loading states. */
export const SkeletonLine = ({ className = "h-4 w-3/4" }) => (
  <div className={`animate-pulse rounded bg-gray-300 ${className}`} />
);
